import React, { useState, useMemo } from "react";
import Button from "../../common/Button/Button";
import InputLogo from "../../common/InputLogo/InputLogo";
import Input from "../../common/Input/Input";
import { Toaster } from "../../common/Toaster/Toaster";
import { useNavigate } from "react-router-dom";
import { registerUser } from "../../service/AuthWellDhanService";
import { useUtils } from "../../context/UtilsContext";
import { validateEmailWithFlexDomain } from "../../utils/validation.util";

function SignUpComponent() {
  const { sessionUtils, profileUtils, loading: utilsLoading } = useUtils();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    username: "",
    date_of_birth: "",
    phone_ext_id: "", // default to ""
    gender_id: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [errorType, setErrorType] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const phoneExtensions = useMemo(
    () => profileUtils?.phone_extensions || [],
    [profileUtils?.phone_extensions]
  );
  const genders = useMemo(
    () => profileUtils?.genders || [],
    [profileUtils?.genders]
  );
  React.useEffect(() => {
    if (phoneExtensions.length && !formData.phone_ext_id) {
      setFormData((prev) => ({ ...prev, phone_ext_id: phoneExtensions[0].id }));
    }
    if (genders.length && !formData.gender_id) {
      setFormData((prev) => ({ ...prev, gender_id: genders[0].id }));
    }
  }, [phoneExtensions, genders, formData.phone_ext_id, formData.gender_id]); // runs once on load

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Get IDs from utils (no hardcoding!)
  const eventTypesArr = sessionUtils?.event_types?.[0]?.event_types || [];
  const appVersion = sessionUtils?.app_versions?.[0];
  const tos = sessionUtils?.terms_of_service?.[0];
  const signin_mode_id = sessionUtils?.signin_modes?.find(x => x.description === "welldhan_id")?.id;
  const welldhan_login_type_id = sessionUtils?.well_dhan_login_types?.find(x => x.description === "email")?.id;
  const event_type_id = eventTypesArr.find(x => x.description === "register_user")?.id;
  const verify_otp_event_type_id = eventTypesArr.find(x => x.description === "verify_otp")?.id;

  // Final handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setErrorType("");
    setLoading(true);

    const emailError = validateEmailWithFlexDomain(formData.email);
    if (emailError) {
      setErrorMessage(emailError);
      setErrorType("error");
      setLoading(false);
      return;
    }

    // Prepare the payload as per backend requirement
    const payload = {
      company_name: "flexcellence",
      application_name: "flexboard",
      event_type_id,
      signin_mode_id,
      welldhan_login_type_id,
      app_version_id: appVersion?.id,
      tos_id: tos?.id,
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      phone_ext_id: formData.phone_ext_id,
      phone: formData.phone,
      username: formData.username,
      gender_id: formData.gender_id,
      date_of_birth: formData.date_of_birth,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
      google_id_token: "null"
    };

    const result = await registerUser(payload);
    setLoading(false);

    if (result.success) {
      if (result.data?.data?.access_token) {
        localStorage.setItem('access_token', result.data.data.access_token);
      }
      // Redirect to OTP page
      navigate("/otp", {
        state: {
          email: formData.email,
          type: "register",
          event_type_id: verify_otp_event_type_id
        }
      });
    } else {
      setErrorMessage(result.message);
      setErrorType("error");
    }
  };

  if (utilsLoading || !sessionUtils || !profileUtils) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full min-h-screen flex flex-col items-center justify-center">
      {errorMessage && <Toaster message={errorMessage} type={errorType} />}
    <div className="bg-app-bg w-full max-w-lg rounded-2xl shadow-lg p-8 flex flex-col items-center">
      <InputLogo text="Sign Up" />
      <form className="w-full flex flex-col items-center" onSubmit={handleSubmit}>
        <Input name="first_name" value={formData.first_name} onChange={handleInputChange} placeholder="Enter First Name" className="w-1/2 bg-input-bg" required />
        <Input name="last_name" value={formData.last_name} onChange={handleInputChange} placeholder="Enter Last Name" className="w-1/2 bg-input-bg mt-4" required />
        <Input name="email" value={formData.email} onChange={handleInputChange} placeholder="Enter Email" validate={validateEmailWithFlexDomain} className="w-1/2 bg-input-bg mt-4" required />
        <div className="flex w-1/2 gap-2 mt-4">
          <select
            name="phone_ext_id"
            value={formData.phone_ext_id}
            onChange={handleInputChange}
            className="bg-input-bg rounded-lg px-4 py-2 min-w-[80px] border border-gray-300 focus:outline-none"
            required
          >
            {phoneExtensions.map((ext) => (
              <option key={ext.id} value={ext.id}>{ext.description}</option>
            ))}
          </select>
          <Input
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="Phone"
            className="flex-1 bg-input-bg"
            required
          />
        </div>
        <Input name="username" value={formData.username} onChange={handleInputChange} placeholder="Username" className="w-1/2 bg-input-bg mt-4" required />
        <select
          name="gender_id"
          value={formData.gender_id}
          onChange={handleInputChange}
          className="bg-input-bg rounded-lg px-4 py-2 mt-4 border border-gray-300 focus:outline-none w-1/2"
          required
        >
          {genders.map((gen) => (
            <option key={gen.id} value={gen.id}>{gen.description}</option>
          ))}
        </select>
        <Input name="date_of_birth" value={formData.date_of_birth} onChange={handleInputChange} placeholder="YYYY-MM-DD" className="w-1/2 bg-input-bg mt-4" required type="date" />
        <Input name="password" value={formData.password} onChange={handleInputChange} placeholder="Password" className="w-1/2 bg-input-bg mt-4" required type="password" />
        <Input name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} placeholder="Confirm Password" className="w-1/2 bg-input-bg mt-4" required type="password" />

        {loading ? (
          <span className="loader mt-5"></span>
        ) : (
          <Button type="submit" text="Signup" className="bg-orange-primary text-white w-1/2 mt-8" />
        )}
      </form>
      <div className="w-full text-center mt-4 text-sm">
  <p>
    Already have an account?{" "}
    <span
      className="text-orange-primary cursor-pointer underline"
      onClick={() => navigate("/login")}
    >
      Back to Login
    </span>
  </p>
</div>

    </div>
    </div>
  );
}

export default SignUpComponent;
